import { create } from 'zustand';

import { listSharedByMeSessions, listSharedSessions } from '@/lib/api/sessions.client';
import { sessionToHistory } from '@/lib/stores/session-history';
import type { IChatHistory } from '@/lib/types/chat';

/**
 * Where a chat list lives (KAN-1416): the plain chat sidebar, one agent, or one
 * project. An agent's chats show only inside that agent, a project's only
 * inside that project, every other chat in the plain sidebar, "Geteilt mit
 * mir" and "Von mir geteilt" included. The server sorts the chats; a chat
 * whose agent or project the reader may not open comes back in the plain area.
 */
export type ChatArea =
  | { kind: 'plain' }
  | { kind: 'agent'; agentId: string }
  | { kind: 'project'; projectId: string };

export const PLAIN_AREA: ChatArea = { kind: 'plain' };

/** Cache key of an area. */
export const areaKey = (area: ChatArea): string => {
  if (area.kind === 'agent') return `agent:${area.agentId}`;
  if (area.kind === 'project') return `project:${area.projectId}`;
  return 'plain';
};

/** The two shared sections of one area. */
export interface AreaSharedChats {
  area: ChatArea;
  /** "Geteilt mit mir": other people's chats shared with the reader. */
  withMe: IChatHistory[];
  /** "Von mir geteilt": the reader's own chats they have shared. */
  byMe: IChatHistory[];
}

interface SharedChatsState {
  /** One entry per area that has been loaded, keyed by ``areaKey``. */
  byArea: Record<string, AreaSharedChats>;
  /**
   * Load both sections of an area. A load already running for the area is
   * joined unless ``force`` is set — a share that changed a moment ago must
   * not be answered by a request that left before it.
   */
  load: (area: ChatArea, options?: { force?: boolean }) => Promise<void>;
  /** Reload every area held, after a share was given, taken or redeemed. */
  reloadAll: () => void;
  /** Reload only the areas listing ``chatId``, after something happened in it. */
  reloadHolding: (chatId: string) => void;
  /** Drop a chat from every area, once it is deleted. */
  removeChat: (chatId: string) => void;
  /**
   * Forget an area's lists without asking again, once the reader can no
   * longer open its project (KAN-1549): asking would only be refused. A list
   * mounted later loads the area afresh.
   */
  dropArea: (area: ChatArea) => void;
  /** Apply a rename or a pin to a chat wherever an area lists it. */
  patchChat: (chatId: string, patch: Partial<Pick<IChatHistory, 'title' | 'isPinned'>>) => void;
  reset: () => void;
}

// The request in flight per area. Only the latest one may write its answer,
// so a slow, older response cannot overwrite a newer one.
const requests = new Map<string, { seq: number; promise: Promise<void> }>();
let lastSeq = 0;

const holdsChat = (entry: AreaSharedChats, chatId: string): boolean =>
  entry.withMe.some((chat) => chat.id === chatId) || entry.byMe.some((chat) => chat.id === chatId);

// Rewrite both lists of every area that holds ``chatId``. Areas without it
// keep their identity, and nothing changes at all when no area holds it, so a
// delete event for a chat we never listed re-renders nothing.
const updateAreasHolding = (
  byArea: Record<string, AreaSharedChats>,
  chatId: string,
  rewrite: (list: IChatHistory[]) => IChatHistory[]
): { byArea: Record<string, AreaSharedChats> } | Record<string, never> => {
  if (!Object.values(byArea).some((entry) => holdsChat(entry, chatId))) return {};
  const next: Record<string, AreaSharedChats> = {};
  for (const [key, entry] of Object.entries(byArea)) {
    next[key] = holdsChat(entry, chatId)
      ? { ...entry, withMe: rewrite(entry.withMe), byMe: rewrite(entry.byMe) }
      : entry;
  }
  return { byArea: next };
};

/** An area as the list endpoints take it: ``agentId`` or ``projectId``, neither for the plain sidebar. */
export const areaQuery = (area: ChatArea) => ({
  agentId: area.kind === 'agent' ? area.agentId : undefined,
  projectId: area.kind === 'project' ? area.projectId : undefined,
});

const fetchArea = async (area: ChatArea): Promise<AreaSharedChats> => {
  const query = areaQuery(area);
  const [withMe, byMe] = await Promise.all([
    listSharedSessions(query),
    listSharedByMeSessions(query),
  ]);
  return {
    area,
    withMe: withMe.map(sessionToHistory),
    byMe: byMe.map(sessionToHistory),
  };
};

export const useSharedChatsStore = create<SharedChatsState>()((set, get) => ({
  byArea: {},

  load: (area, options) => {
    const key = areaKey(area);
    const running = requests.get(key);
    if (running && !options?.force) return running.promise;
    const seq = ++lastSeq;
    const isLatest = () => requests.get(key)?.seq === seq;
    const promise = fetchArea(area)
      .then((lists) => {
        if (isLatest()) set((state) => ({ byArea: { ...state.byArea, [key]: lists } }));
      })
      // A failed load keeps what the area showed before; the next event or
      // reconnect tries again.
      .catch(() => undefined)
      .finally(() => {
        if (isLatest()) requests.delete(key);
      });
    requests.set(key, { seq, promise });
    return promise;
  },

  reloadAll: () => {
    for (const { area } of Object.values(get().byArea)) {
      void get().load(area, { force: true });
    }
  },

  reloadHolding: (chatId) => {
    for (const entry of Object.values(get().byArea)) {
      if (holdsChat(entry, chatId)) void get().load(entry.area, { force: true });
    }
  },

  removeChat: (chatId) => {
    set(({ byArea }) =>
      updateAreasHolding(byArea, chatId, (list) => list.filter((c) => c.id !== chatId))
    );
  },

  dropArea: (area) => {
    const key = areaKey(area);
    requests.delete(key);
    set(({ byArea }) =>
      key in byArea
        ? { byArea: Object.fromEntries(Object.entries(byArea).filter(([held]) => held !== key)) }
        : {}
    );
  },

  patchChat: (chatId, patch) => {
    set(({ byArea }) =>
      updateAreasHolding(byArea, chatId, (list) =>
        list.map((chat) => (chat.id === chatId ? { ...chat, ...patch } : chat))
      )
    );
  },

  reset: () => {
    requests.clear();
    set({ byArea: {} });
  },
}));

/** Whether any loaded area lists ``chatId`` under "Geteilt mit mir". */
export const isSharedWithMe = (chatId: string): boolean =>
  Object.values(useSharedChatsStore.getState().byArea).some((entry) =>
    entry.withMe.some((chat) => chat.id === chatId)
  );
